import { useEffect, useState } from "react";
import { Link, useParams, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, ShoppingCart, ExternalLink, Truck, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { startCoinPayment } from "@/lib/coinpay";
import ShareMenu from "@/components/ShareMenu";
import ProductGallery from "@/components/ProductGallery";
import VariantPicker, { variantComplete, productImages, colorPhotoIndex, variantPrice } from "@/components/VariantPicker";
import { captureRef, shareProductUrl, getRef } from "@/lib/shareRef";
import ShareEarnHint from "@/components/ShareEarnHint";
import ReturnPolicyButton from "@/components/ReturnPolicy";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SITE = "https://www.picklebullpen.com";

const setMeta = (attr, key, content) => {
  let el = document.querySelector(`meta[${attr}="${key}"]`);
  if (!el) { el = document.createElement("meta"); el.setAttribute(attr, key); document.head.appendChild(el); }
  el.setAttribute("content", content);
};

export default function ProductPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const cart = useCart();
  const [p, setP] = useState(null);
  const [missing, setMissing] = useState(false);
  const [sp] = useSearchParams();
  const [v, setV] = useState(() => ({ ...(sp.get("option") ? { option: sp.get("option") } : {}), ...(sp.get("size") ? { size: sp.get("size") } : {}), ...(sp.get("color") ? { color: sp.get("color") } : {}) }));
  const id = (slug || "").split("-").pop();

  useEffect(() => { captureRef(sp.get("ref"), id); }, [sp, id]);

  useEffect(() => {
    axios.get(`${API}/store/products/${id}`).then(({ data }) => {
      setP(data);
      const title = `${data.name} — $${data.price} | Pickle Bullpen Pro Shop`;
      const desc = `${data.desc || data.name}${data.sizes?.length ? ` · Sizes ${data.sizes.join("/")}` : ""}${data.colors?.length ? ` · Colors ${data.colors.join("/")}` : ""}. Pickleball gear from Pickle Bullpen${data.company ? ` · ${data.company}` : ""}.`.slice(0, 300);
      const canonical = `${SITE}${data.path}`;
      document.title = title;
      setMeta("name", "description", desc);
      setMeta("name", "keywords", `${data.name}, ${data.category} pickleball, ${data.company || "Pickle Bullpen"} ${data.category}, buy pickleball ${data.category.toLowerCase()}, pickleball gear Florida, Pickle Bullpen Pro Shop`);
      setMeta("property", "og:title", title); setMeta("property", "og:description", desc);
      setMeta("property", "og:url", canonical); setMeta("property", "og:type", "product");
      setMeta("property", "og:image", `${API}/share/products/${data.id}/image`);
      setMeta("property", "product:price:amount", String(data.price)); setMeta("property", "product:price:currency", "USD");
      let link = document.querySelector('link[rel="canonical"]');
      if (!link) { link = document.createElement("link"); link.setAttribute("rel", "canonical"); document.head.appendChild(link); }
      link.setAttribute("href", canonical);
      const ld = {
        "@context": "https://schema.org", "@type": "Product", name: data.name, description: data.desc || data.name,
        image: productImages(data).map((_, i) => `${API}/share/products/${data.id}/image?i=${i}`),
        sku: data.id, category: data.category, brand: { "@type": "Brand", name: data.company || "Pickle Bullpen" },
        ...(data.colors?.length ? { color: data.colors.join(", ") } : {}), ...(data.sizes?.length ? { size: data.sizes.join(", ") } : {}),
        offers: { "@type": "Offer", url: canonical, priceCurrency: "USD", price: data.price,
          availability: data.sold_out ? "https://schema.org/OutOfStock" : "https://schema.org/InStock",
          seller: { "@type": "Organization", name: "Pickle Bullpen" } },
      };
      let s = document.getElementById("product-ld");
      if (!s) { s = document.createElement("script"); s.id = "product-ld"; s.type = "application/ld+json"; document.head.appendChild(s); }
      s.textContent = JSON.stringify(ld);
    }).catch(() => setMissing(true));
    return () => { document.getElementById("product-ld")?.remove(); };
  }, [id]);

  if (missing) return (
    <div className="min-h-screen bg-[#0A0A0A] text-white flex flex-col items-center justify-center gap-4" data-testid="product-not-found">
      <p className="uppercase tracking-widest text-zinc-500">This item is no longer in the Pro Shop</p>
      <Link to="/shops" className="text-lime-400 text-sm underline">Browse the Bullpen Marketplace</Link>
    </div>
  );
  if (!p) return <div className="min-h-screen bg-[#0A0A0A]" />;

  const ready = variantComplete(p, v);
  const needPick = () => { toast.info("Pick your size / color first"); };
  const shareUrl = shareProductUrl(p.id, user?.id);
  const add = () => { if (!ready) return needPick(); cart.addProduct(p, v); cart.setOpen(true); };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white px-5 sm:px-8 py-8" data-testid="product-page">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between gap-3 mb-6">
          <Link to={user ? "/store" : "/"} data-testid="product-back" className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-zinc-500 hover:text-lime-400 transition-colors">
            <ArrowLeft className="w-3.5 h-3.5" /> <img src="/icons/proshop-sm.png" alt="" className="w-8 h-8" /> {user ? "Back to the Pro Shop" : "PBPA Pro Shop"}
          </Link>
          <div className="flex items-center gap-3">
            {user && <ShareEarnHint testid="product-page-earn" />}
            <ShareMenu url={shareUrl} text={`🏓 ${p.name} — $${p.price} at the Pickle Bullpen Pro Shop`} image={`${API}/share/products/${p.id}/image`} testId="product-page-share" label="Share this item" placement="below" />
          </div>
        </div>
        <div className="grid md:grid-cols-[3fr_2fr] gap-8">
          <ProductGallery images={productImages(p)} alt={p.name} fit="contain" jumpTo={colorPhotoIndex(p, v)} grayscale={p.sold_out}
            className="aspect-square bg-black border border-zinc-800" testid="product-page-gallery" />
          <div className="flex flex-col">
            <p className="font-mono text-lime-300 uppercase tracking-[0.3em] text-xs mb-3">Pickle Bullpen Pro Shop · {p.category}{p.company ? ` · ${p.company}` : ""}</p>
            <h1 className="font-display font-black uppercase leading-[0.95] text-4xl sm:text-5xl mb-4" data-testid="product-page-name">{p.name}</h1>
            <div className="flex items-baseline gap-3 mb-5">
              <span className="font-display font-black text-4xl text-lime-400" data-testid="product-page-price">{p.has_options && !v.option ? <span className="text-xs text-zinc-500 font-sans font-normal uppercase tracking-widest mr-2">from</span> : null}${variantPrice(p, v)}</span>
              {p.on_sale && <span className="text-zinc-500 line-through">${p.original_price}</span>}
              {p.sold_out ? <span className="text-red-400 text-xs font-black uppercase tracking-widest border border-red-500 px-2 py-1">Sold Out</span>
                : p.low ? <span className="text-amber-300 text-[10px] uppercase tracking-widest">only {p.stock_quantity} left</span> : null}
            </div>
            <p className="text-zinc-300 whitespace-pre-line leading-relaxed mb-6" data-testid="product-page-desc">{p.desc}</p>
            <div className="mb-6"><VariantPicker product={p} value={v} onChange={setV} size="lg" testid="product-page-variant" /></div>
            <div className="mt-auto space-y-2">
              {p.ambassador_link ? (
                <a href={p.ambassador_link} target="_blank" rel="noopener noreferrer" data-testid="product-page-buynow"
                  className="w-full flex items-center justify-center gap-2 bg-lime-400 text-black py-3.5 text-xs font-black uppercase tracking-widest hover:bg-lime-300 transition-colors">
                  Buy Now <ExternalLink className="w-4 h-4" />
                </a>
              ) : user ? (
                <>
                  <button data-testid="product-page-add" disabled={p.sold_out} onClick={add}
                    className="w-full flex items-center justify-center gap-2 bg-lime-400 text-black py-3.5 text-xs font-black uppercase tracking-widest hover:bg-lime-300 active:scale-[0.98] transition-all disabled:opacity-40">
                    <ShoppingCart className="w-4 h-4" /> {p.sold_out ? "Sold Out" : ready ? "Add to Cart" : "Choose options to add"}
                  </button>
                  {p.price_coins && (
                    <button data-testid="product-page-coin" disabled={p.sold_out} onClick={() => (ready ? startCoinPayment(navigate, { kind: "product", item_id: p.id, payload: { variant: v, ref: getRef(user?.id) } }) : needPick())}
                      className="w-full border border-purple-400/60 text-purple-300 hover:bg-purple-400/10 font-bold uppercase tracking-widest py-3 text-xs transition-colors disabled:opacity-40">
                      🪙 Buy with Coin · {p.price_coins} BLC
                    </button>
                  )}
                </>
              ) : (
                <Link to={`/auth?next=${encodeURIComponent(p.path)}`} data-testid="product-page-signin"
                  className="w-full flex items-center justify-center gap-2 bg-lime-400 text-black py-3.5 text-xs font-black uppercase tracking-widest hover:bg-lime-300 transition-colors">
                  <ShoppingCart className="w-4 h-4" /> Sign in / join free to buy
                </Link>
              )}
              <div className="flex flex-wrap gap-4 pt-3 text-[10px] uppercase tracking-widest text-zinc-500">
                {p.requires_shipping && <span className="flex items-center gap-1.5"><Truck className="w-3.5 h-3.5 text-lime-400" /> Ships anywhere in the US</span>}
                <span className="flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5 text-lime-400" /> Secure checkout by Stripe</span>
              </div>
              <ReturnPolicyButton light testid="product-page-return-policy" className="pt-3" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
